import { useState, useMemo } from 'react';
import { ProyectoMock } from '../../../mocks/proyectos';
import DashboardCharts from './DashboardCharts';

type FilterStatus = 'todos' | string;

const estadoBadge: Record<string, string> = {
  'En ejecución': 'bg-teal-50 text-teal-700 border-teal-200',
  'Aprobado': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Finalizado': 'bg-slate-100 text-slate-600 border-slate-200',
  'En evaluación': 'bg-amber-50 text-amber-700 border-amber-200',
  'Con correcciones': 'bg-orange-50 text-orange-700 border-orange-200',
  'No pertinente': 'bg-red-50 text-red-600 border-red-200',
  'Pendiente': 'bg-slate-50 text-slate-500 border-slate-200',
};

const statsCards = [
  {
    label: 'Total recibidos',
    filter: 'todos',
    icon: 'ri-file-text-line',
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    border: 'border-teal-100',
  },
  {
    label: 'En evaluación',
    filter: 'En evaluación',
    icon: 'ri-microscope-line',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
  },
  {
    label: 'Aprobados / En ejecución',
    filter: 'Aprobado',
    icon: 'ri-checkbox-circle-line',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
  },
  {
    label: 'Con correcciones',
    filter: 'Con correcciones',
    icon: 'ri-edit-circle-line',
    color: 'text-orange-500',
    bg: 'bg-orange-50',
    border: 'border-orange-100',
  },
  {
    label: 'No pertinentes',
    filter: 'No pertinente',
    icon: 'ri-close-circle-line',
    color: 'text-red-500',
    bg: 'bg-red-50',
    border: 'border-red-100',
  },
];

function ProgressBar({ value }: { value: number }) {
  const color =
    value === 100
      ? 'bg-emerald-500'
      : value >= 50
      ? 'bg-teal-500'
      : value > 0
      ? 'bg-amber-400'
      : 'bg-slate-200';
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 bg-slate-100 rounded-full h-1.5">
        <div className={`${color} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs text-slate-500 w-8 text-right">{value}%</span>
    </div>
  );
}

interface Props {
  proyectos: ProyectoMock[];
}

export default function Dashboard({ proyectos }: Props) {
  const [activeFilter, setActiveFilter] = useState<FilterStatus>('todos');
  const [search, setSearch] = useState('');
  const [showCharts, setShowCharts] = useState(true);

  const countByState = useMemo(() => {
    const map: Record<string, number> = {};
    proyectos.forEach((p) => {
      map[p.estado] = (map[p.estado] || 0) + 1;
    });
    return map;
  }, [proyectos]);

  const getCount = (filter: string) => {
    if (filter === 'todos') return proyectos.length;
    if (filter === 'Aprobado')
      return (countByState['Aprobado'] || 0) + (countByState['En ejecución'] || 0) + (countByState['Finalizado'] || 0);
    return countByState[filter] || 0;
  };

  const filtered = useMemo(() => {
    let list = proyectos;
    if (activeFilter !== 'todos') {
      if (activeFilter === 'Aprobado') {
        list = list.filter((p) => ['Aprobado', 'En ejecución', 'Finalizado'].includes(p.estado));
      } else {
        list = list.filter((p) => p.estado === activeFilter);
      }
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.titulo.toLowerCase().includes(q) ||
          p.investigador.toLowerCase().includes(q) ||
          p.codigo.toLowerCase().includes(q) ||
          p.area.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeFilter, search, proyectos]);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-md">
              <i className="ri-dashboard-line text-white text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Panel de Control</h2>
          </div>
          <button
            onClick={() => setShowCharts((v) => !v)}
            className="whitespace-nowrap flex items-center gap-1.5 text-sm text-slate-600 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer transition-all"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className={showCharts ? 'ri-eye-off-line' : 'ri-eye-line'} />
            </span>
            {showCharts ? 'Ocultar gráficas' : 'Ver gráficas'}
          </button>
        </div>
        <p className="text-slate-500 text-sm ml-11">
          Resumen ejecutivo de todos los proyectos de investigación · Ciclo 2025–2026
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {statsCards.map((s) => {
          const count = getCount(s.filter);
          const active = activeFilter === s.filter;
          return (
            <button
              key={s.filter}
              onClick={() => setActiveFilter(s.filter)}
              className={`cursor-pointer text-left p-4 rounded-lg border transition-all ${
                active
                  ? `${s.bg} ${s.border} border-2`
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-5 h-5 flex items-center justify-center ${active ? s.color : 'text-slate-400'}`}>
                  <i className={`${s.icon} text-base`} />
                </span>
              </div>
              <p className={`text-2xl font-bold ${active ? s.color : 'text-slate-800'}`}>{count}</p>
              <p className={`text-xs mt-0.5 ${active ? s.color : 'text-slate-500'}`}>{s.label}</p>
            </button>
          );
        })}
      </div>

      {/* Charts */}
      {showCharts && <DashboardCharts proyectos={proyectos} />}

      {/* Filters + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-list-unordered text-teal-600" />
          </span>
          {filtered.length} proyecto{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
        </h3>
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
            <i className="ri-search-line text-sm" />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código, título, investigador..."
            className="pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-md bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-500 w-72"
          />
        </div>
      </div>

      {/* Projects table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {['Código', 'Título', 'Investigador', 'Área', 'Categoría', 'Estado', 'Ética', 'Avance', 'Recepción', 'Decisión'].map(
                (h) => (
                  <th
                    key={h}
                    className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
                  >
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-slate-400 text-sm">
                  No se encontraron proyectos con los filtros seleccionados
                </td>
              </tr>
            ) : (
              filtered.map((p) => (
                <tr key={p.codigo} className="bg-white hover:bg-slate-50 transition-colors">
                  <td className="px-3 py-3 font-mono text-xs font-semibold text-teal-700 whitespace-nowrap">
                    {p.codigo}
                  </td>
                  <td className="px-3 py-3 text-slate-700 max-w-[200px]">
                    <span className="block truncate text-xs font-medium" title={p.titulo}>
                      {p.titulo}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-slate-600 text-xs whitespace-nowrap">{p.investigador}</td>
                  <td className="px-3 py-3 text-slate-500 text-xs max-w-[120px]">
                    <span className="truncate block" title={p.area}>{p.area}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs bg-teal-50 text-teal-700 border border-teal-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      {p.categoria}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${
                        estadoBadge[p.estado] || 'bg-slate-50 text-slate-500 border-slate-200'
                      }`}
                    >
                      {p.estado}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        p.etica === 'Sí'
                          ? 'bg-teal-50 text-teal-700 border border-teal-100'
                          : 'text-slate-400'
                      }`}
                    >
                      {p.etica}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <ProgressBar value={p.porcentaje} />
                  </td>
                  <td className="px-3 py-3 text-slate-500 text-xs whitespace-nowrap">{p.fechaRecepcion}</td>
                  <td className="px-3 py-3 text-slate-500 text-xs whitespace-nowrap">{p.fechaDecision}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400 mt-3 text-right">
        Total: {proyectos.length} proyectos registrados · Última actualización: {new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
      </p>
    </div>
  );
}
